import { Stripe } from "@stripe/stripe-js";
import React from "react";
import { useUser } from '../../context/UserContext';
import BookIcon from '@mui/icons-material/MenuBook';
import InventoryIcon from '@mui/icons-material/Inventory';
import StarIcon from '@mui/icons-material/Star';
import { CardNumberElement, CardExpiryElement, CardCvcElement, IbanElement, useStripe, useElements, } from '@stripe/react-stripe-js';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useState, useRef, useEffect } from 'react';
import ModalWrapper from "../../components/Modal/ModalWrapper";
// ...
const stripePromise = loadStripe("pk_test_51RSSM7PMDYlN34aJw7zBgSbLNqNdd257sNdBiEx671EYvfZc1kodFH2SQBDRfXqsgsdzwDEgGeFctTkJZDQwNpnR00GLnMtYBY");
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);
const AccessToken = localStorage.getItem("token");

type Plan = {
  name: string;
  headline?: string;
  description: string;
  features: string[];
  price_eur: string;
  stripe_price_id: string;
  rank: number;
  color: string;
  icon_name: string;
};




// Icon mapping for plan icons
const iconMap: Record<string, JSX.Element> = {
  BookIcon: <BookIcon fontSize="small" className="text-white" />,
  InventoryIcon: <InventoryIcon fontSize="small" className="text-white" />,
  StarIcon: <StarIcon fontSize="small" className="text-white" />,
};


function shadeColor(color: string, percent: number) {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return "#" + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}

const PlanPicker: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);
  const [showCheckoutForm, setShowCheckoutForm] = React.useState(false);
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const { user, loading } = useUser();
  const { updateUser } = useUser();
  const { refreshUser } = useUser();
  useEffect(() => {
    if (!user) {
      refreshUser();
    }
  }, []);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const plansRef = useRef<HTMLDivElement | null>(null);
  const status = user?.subscription?.status ?? "";
  const planName = user?.subscription?.plan_name ?? (status === "free" || user?.subscription?.is_virtual_fallback ? "Free Plan" : "Kein Plan");
  const isProbeModel = /probe|demo|trial|test/i.test(planName);
  const isPilot = /Beta/i.test(planName);
  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const handleCancelSubscription = async () => {
    try {
      const response = await fetch("/api/subscriptions/cancel-subscription/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        alert("Ihr Abonnement wird zum Ende des Abrechnungszeitraums beendet.");
        await refreshUser();
      } else {
        alert(data.error || "Fehler bei der Kündigung.");
      }
      setShowCancelDialog(false);
      setCancelConfirmed(false);
    } catch (error) {
      console.error("Fehler bei der Kündigung:", error);
    }
  };

  useEffect(() => {
    const syncSubscription = async () => {
      if (!user) {
        console.log("⚠️ Kein User vorhanden, Sync abgebrochen.");
        return;
      }
      try {
        console.log("🔄 Syncing Stripe subscription...");
        await fetch("/api/subscriptions/sync-subscription/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        await refreshUser(); // danach Userdaten neu laden
        console.log("✅ Stripe subscription synced and user refreshed.");
      } catch (error) {
        console.error("Fehler bei der Synchronisierung mit Stripe:", error);
      }
    };

    syncSubscription(); // nur beim Öffnen der Seite ausführen

  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch("/api/subscriptions/list-subscription-plans");
        const data = await response.json();
        setPlans(data);
      } catch (error) {
        console.error("Fehler beim Laden der Pläne:", error);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Modal for unavailable booking
  const [modal, setModal] = React.useState<React.ReactNode | null>(null);
  function handlePlanSelect(plan: Plan) {
    if (!siteSettings || !siteSettings.allow_user_subscription) {
      setModal(
        <ModalWrapper onClose={() => setModal(null)} title="Buchung demnächst verfügbar">
          <p>
            Die Buchungsfunktion ist derzeit noch deaktiviert.
            Ab dem <strong>15. November</strong> können Sie reguläre Abo-Modelle buchen. {" "}
          </p>
          <p>
            Bis dahin steht Ihnen Ihr Testzugang uneingeschränkt zur Verfügung – vielen Dank für Ihre Geduld!
          </p>
          <div className="tw-flex tw-justify-center tw-mt-6">
            <button
              onClick={() => setModal(null)}
              className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
            >
              OK
            </button>
          </div>
        </ModalWrapper>
      );
      return;
    }
    setSelectedPlan(plan);
    setShowCheckoutForm(false);
  }


  // Stripe CheckoutForm Komponente
  const CheckoutForm = ({ user }: { user: any }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [modal, setModal] = React.useState<React.ReactNode | null>(null);
    const { refreshUser } = useUser();
    const [paymentMethod, setPaymentMethod] = useState<"card" | "sepa_debit">("card");



    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      if (!stripe || !elements) return;

      setModal(<ModalWrapper onClose={() => setModal(null)} title="Zahlung wird verarbeitet...">
        <p>Bitte warten Sie einen Moment. Ihre Zahlung wird durchgeführt.</p>
      </ModalWrapper>);

      let paymentMethodObj: any = null;
      let error: any = null;

      if (paymentMethod === "card") {
        const cardElement = elements.getElement(CardNumberElement) as import('@stripe/stripe-js').StripeCardNumberElement | null;
        if (!cardElement) {
          setModal(null);
          return;
        }
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        error = result.error;
        paymentMethodObj = result.paymentMethod;
      } else if (paymentMethod === "sepa_debit") {
        const ibanElement = elements.getElement(IbanElement) as import('@stripe/stripe-js').StripeIbanElement | null;
        if (!ibanElement) {
          setModal(null);
          return;
        }
        const result = await stripe.createPaymentMethod({
          type: 'sepa_debit',
          sepa_debit: ibanElement,
          billing_details: {
            name: user?.full_name || user?.email,
            email: user?.email,
          },
        });
        error = result.error;
        paymentMethodObj = result.paymentMethod;
      }

      if (error) {
        setModal(null);
        console.error(error);
        return;
      }

      // PaymentMethod created, send to backend
      const response = await fetch("/api/subscriptions/create-subscription/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethodObj.id,
          priceId: selectedPlan?.stripe_price_id,
          customerId: user?.stripe_customer_id || null,
        }),
      });

      const data = await response.json();

      // NEU: Stripe Customer ID im UserContext und in DB speichern, falls neu
      console.log("Subscription creation response:", data);
      if (data.customerId && data.customerId !== user?.stripe_customer_id) {
        await updateUser({ stripe_customer_id: data.customerId });
      }

      // NEU: ClientSecret-Check für automatische Up-/Downgrades ohne Zahlung
      if (!data.clientSecret) {
        console.log("⚠️ Kein ClientSecret vorhanden – wahrscheinlich automatisches Upgrade oder Downgrade ohne zusätzliche Zahlung.");
        setModal(
          <ModalWrapper onClose={() => {
            setModal(null);
            setShowCheckoutForm(false);
            setSelectedPlan(null);
            refreshUser();
          }}
            title="Abo erfolgreich aktualisiert">
            <p>
              Ihr Abonnement wurde erfolgreich geändert. Unser Zahlungsdienstleister (Stripe) hat die neue Abrechnung automatisch übernommen.
            </p>
            <div className="tw-flex tw-justify-center tw-mt-6">
              <button
                onClick={() => {
                  setModal(null);
                  setShowCheckoutForm(false);
                  setSelectedPlan(null);
                  refreshUser();
                }}
                className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
              >
                OK
              </button>
            </div>
          </ModalWrapper>
        );
        return;
      }

      if (data.error) {
        setModal(
          <ModalWrapper onClose={() => {
            setModal(null);
            setShowCheckoutForm(false);
            setSelectedPlan(null);
            refreshUser();
          }} title="Fehler beim Abschluss">
            <p>
              Es gab einen Fehler bei der Buchung Ihres Abonnements. Bitte wenden Sie sich an unser <a href="https://lumanote.de/contact" target="_blank" rel="noreferrer" className="text-blue-600 underline">Support-Team</a>.
            </p>
            <div className="tw-flex tw-justify-center tw-mt-6">
              <button
                onClick={() => {
                  setModal(null);
                  setShowCheckoutForm(false);
                  setSelectedPlan(null);
                  refreshUser();
                }}
                className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
              >
                OK
              </button>
            </div>
          </ModalWrapper>
        );
        console.error("Subscription creation failed:", data.error);
        return;
      }

      if (paymentMethod === "card") {
        const result = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: paymentMethodObj.id,
        });
        if (result.error) {
          setModal(null);
          console.error(result.error.message);
          return;
        }
        if (result.paymentIntent.status === "succeeded") {
          setModal(
            <ModalWrapper onClose={() => {
              setModal(null);
              setShowCheckoutForm(false);
              setSelectedPlan(null);
              refreshUser();
            }} title="Zahlung erfolgreich">
              <p>
                Ihre Zahlung war erfolgreich. Ihr Abo wird gerade aktiviert. Bitte warten Sie einen Moment – dieser Vorgang kann bis zu 1 Minute dauern.
              </p>
              <div className="tw-flex tw-justify-center tw-mt-6">
                <button
                  onClick={() => {
                    setModal(null);
                    setShowCheckoutForm(false);
                    setSelectedPlan(null);
                    refreshUser();
                  }}
                  className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
                >
                  OK
                </button>
              </div>
            </ModalWrapper>
          );
        }
      } else if (paymentMethod === "sepa_debit") {
        // SEPA-Lastschrift: confirm the payment if clientSecret is present
        if (data.clientSecret) {
          const result = await stripe.confirmSepaDebitPayment(data.clientSecret, {
            payment_method: paymentMethodObj.id,
          });
          if (result.error) {
            setModal(null);
            console.error(result.error.message);
            return;
          }
          if (
            result.paymentIntent.status === "processing" ||
            result.paymentIntent.status === "succeeded"
          ) {
            setModal(
              <ModalWrapper onClose={() => {
                setModal(null);
                setShowCheckoutForm(false);
                setSelectedPlan(null);
                refreshUser();
              }} title="Zahlung eingereicht">
                <p>
                  Ihre SEPA-Lastschrift wurde eingereicht. Ihr Abo wird aktiviert, sobald die Zahlung abgeschlossen ist. Sie erhalten eine Bestätigung per E-Mail.
                </p>
                <div className="tw-flex tw-justify-center tw-mt-6">
                  <button
                    onClick={() => {
                      setModal(null);
                      setShowCheckoutForm(false);
                      setSelectedPlan(null);
                      refreshUser();
                    }}
                    className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
                  >
                    OK
                  </button>
                </div>
              </ModalWrapper>
            );
          }
        } else {
          setModal(
            <ModalWrapper onClose={() => {
              setModal(null);
              setShowCheckoutForm(false);
              setSelectedPlan(null);
              refreshUser();
            }} title="Zahlung eingereicht">
              <p>
                Ihre SEPA-Lastschrift wurde eingereicht. Sie erhalten eine Bestätigung per E-Mail.
              </p>
              <div className="tw-flex tw-justify-center tw-mt-6">
                <button
                  onClick={() => {
                    setModal(null);
                    setShowCheckoutForm(false);
                    setSelectedPlan(null);
                    refreshUser();
                  }}
                  className="tw-bg-primary-500 tw-text-white tw-rounded tw-px-4 tw-py-2 tw-shadow hover:tw-bg-primary-600"
                >
                  OK
                </button>
              </div>
            </ModalWrapper>
          );
        }
      }
    };

    if (modal) return <>{modal}</>;

    return (
      <div className="tw-w-full tw-pt-6 tw-pb-32 sm:tw-pb-0 tw-overflow-y-scroll scroll-hidden max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="mt-6 space-y-4 w-full max-w-2xl mx-auto">
          <div className="space-y-4 w-full">
            {/* Zahlungsmethode Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Zahlungsmethode</label>
              <select
                className="border p-3 rounded w-full focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value as "card" | "sepa_debit")}
              >
                <option value="card">Kreditkarte</option>
                <option value="sepa_debit">SEPA-Lastschrift (IBAN)</option>
              </select>
            </div>
            {/* Kreditkarte Felder */}
            {paymentMethod === "card" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kartennummer</label>
                  <div className="border p-3 rounded">
                    <CardNumberElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#32325d",
                            fontFamily: "Arial, sans-serif",
                            '::placeholder': {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#fa755a",
                            iconColor: "#fa755a",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700">Ablaufdatum</label>
                    <div className="border p-3 rounded">
                      <CardExpiryElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#32325d",
                              fontFamily: "Arial, sans-serif",
                              '::placeholder': {
                                color: "#aab7c4",
                              },
                            },
                            invalid: {
                              color: "#fa755a",
                              iconColor: "#fa755a",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700">CVC</label>
                    <div className="border p-3 rounded">
                      <CardCvcElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#32325d",
                              fontFamily: "Arial, sans-serif",
                              '::placeholder': {
                                color: "#aab7c4",
                              },
                            },
                            invalid: {
                              color: "#fa755a",
                              iconColor: "#fa755a",
                            },
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* SEPA Felder */}
            {paymentMethod === "sepa_debit" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">IBAN</label>
                <div className="border p-3 rounded">
                  <IbanElement
                    options={{
                      supportedCountries: ['SEPA'],
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#32325d",
                          fontFamily: "Arial, sans-serif",
                          '::placeholder': {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#fa755a",
                          iconColor: "#fa755a",
                        },
                      },
                    }}
                  />
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Mit der Angabe Ihrer IBAN erteilen Sie LumaNote GbR ein SEPA-Lastschriftmandat. Sie werden vor der Belastung per E-Mail informiert.
                </div>
              </div>
            )}
          </div>
          {/* Begin: Subscribe/confirmation UI from payment-form__powered */}
          <div className="payment-form__powered">
            <button data-test-id="checkout-subscribe-btn" type="submit" id="" className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !font-normal"
              style={{
                backgroundImage: "linear-gradient(to right,rgb(13, 55, 69),rgb(76, 146, 170),rgb(121, 172, 188))",
                color: "white",
                border: "none"
              }} disabled={!stripe}>
              <div className="_btnFlex_1uoax_32">Verbindlich buchen</div>
            </button>
            <div className="flex flex-col items-center justify-center font-bold mt-2 text-sm text-gray-500">
              <span>Gesamtbetrag von {selectedPlan?.price_eur}</span>
              <span> Verlängert sich automatisch</span>
            </div>
            <div className="flex flex-col gap-2 pt-3 w-full">
              <div className="text-[#20A961] gap-1 flex items-center justify-center w-full">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame"><path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M8 1C7.07174 1 6.1815 1.36875 5.52513 2.02513C4.86875 2.6815 4.5 3.57174 4.5 4.5V6.5C3.96957 6.5 3.46086 6.71071 3.08579 7.08579C2.71071 7.46086 2.5 7.96957 2.5 8.5V13C2.5 13.5304 2.71071 14.0391 3.08579 14.4142C3.46086 14.7893 3.96957 15 4.5 15H11.5C12.0304 15 12.5391 14.7893 12.9142 14.4142C13.2893 14.0391 13.5 13.5304 13.5 13V8.5C13.5 7.96957 13.2893 7.46086 12.9142 7.08579C12.5391 6.71071 12.0304 6.5 11.5 6.5V4.5C11.5 2.56667 9.93333 1 8 1ZM10.5 6.5V4.5C10.5 3.83696 10.2366 3.20107 9.76777 2.73223C9.29893 2.26339 8.66304 2 8 2C7.33696 2 6.70107 2.26339 6.23223 2.73223C5.76339 3.20107 5.5 3.83696 5.5 4.5V6.5H10.5Z" fill="#20A961"></path></g></svg>
                <span className="font-bold text-base"> Sicherer checkout</span>
              </div>
              <div className="flex items-center justify-center w-full">
                <p className="text-black m-0">
                  <span className="mr-2 text-md font-normal">Powered by STRIPE</span>
                  <svg width="47" height="19" viewBox="0 0 47 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="..." fill="#040404" /></svg>
                  <span className="font-normal"> - </span>
                  <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="https://www.lumanoteEX.com/terms-of-use" target="_blank" rel="noreferrer">Nutzungsbedingungen</a></span>
                  <span className="font-normal"> &amp; </span>
                  <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="https://www.lumanoteEX.com/privacy-policy" target="_blank" rel="noreferrer">Datenschutz</a></span>
                </p>
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {paymentMethod === "card"
                ? "Mit der Angabe Ihrer Kartendaten erklären Sie sich damit einverstanden, dass LumaNote GbR. Ihre Karte für zukünftige Zahlungen gemäß seinen Bedingungen belastet."
                : "Mit der Angabe Ihrer IBAN erteilen Sie LumaNote GbR ein SEPA-Lastschriftmandat. Sie werden vor der Belastung per E-Mail informiert."
              }
            </div>
          </div>
          {/* End: Subscribe/confirmation UI */}
        </form>
      </div>
    );
  };

  // Ladezustand
  if (loading || loadingPlans) return <div className="p-6">Lade...</div>;
  if (!user) return <div className="p-6">Lade Benutzer...</div>;

  return (
    <div className="w-full pt-2 lg:pt-2 pb-32 sm:pb-0 overflow-y-auto overflow-x-auto scrollbar-hide h-[calc(100vh-10rem)] px-4">
      {modal}
      {/* Abonnementstatus anzeigen */}

      {(() => {


        const periodStart = user?.subscription?.current_period_start
          ? new Date(user.subscription.current_period_start).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        const periodEnd = user?.subscription?.current_period_end
          ? new Date(user.subscription.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        const startDate = user?.subscription?.start_date
          ? new Date(user.subscription.start_date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        // Statusanzeige und Farben
        let title = "";
        let statusText = "";
        let statusColor = "tw-text-gray-600";
        let details = "";
        let extraDetails: React.ReactNode = null;
        let extraButtons: React.ReactNode = null;
        let showCancel = false;

        if (["pending", "incomplete"].includes(status)) {
          title = "Zahlung wird verarbeitet";
          statusText = "Status: Zahlung wird verarbeitet";
          statusColor = "tw-text-yellow-600";
          details = `Beginn: ${startDate}\nAktuelle Periode: ${periodStart} – ${periodEnd}\nPreis: ${user?.subscription?.price_display ?? "-"}`;
          extraDetails = (
            <p className="text-sm tw-text-yellow-700">
              Ihre Buchung wurde empfangen und wird derzeit verarbeitet. Sobald die Zahlung bestätigt ist, wird Ihr Abo automatisch aktiviert.
            </p>
          );
        } else if (status === "trialing") {
          title = "Probe-Abo aktiv";
          statusText = user?.subscription?.current_period_end
            ? `Ihr Probe-Abo läuft bis ${periodEnd}.`
            : "Probe-Abo aktiv";
          statusColor = "tw-text-green-600";
          details = "Dieses Probe-Abo endet automatisch und verlängert sich nicht.";
        } else if (status === "staff") {
          title = "Mitarbeiterkonto aktiv";
          statusText = "Sie haben unbegrenzten Zugriff auf alle Funktionen.";
          statusColor = "tw-text-green-600";
          details = "";
        } else if (status === "free" || user?.subscription?.is_virtual_fallback) {
          title = "Kostenloser Plan aktiv";
          statusText = "Status: Aktiv (kostenlos)";
          statusColor = "tw-text-green-600";
          details = "";
          extraDetails = (
            <>
              <p className="text-sm text-gray-500 mb-1">
                Sie nutzen derzeit unseren kostenlosen Basiszugang mit eingeschränkten Funktionen.
              </p>
              <p className="text-sm text-gray-500">
                Sie können jederzeit ein{" "}
                <button
                  type="button"
                  onClick={scrollToPlans}
                  className="text-primary-600 underline hover:text-primary-800"
                >
                  höheres Abo-Modell
                </button>{" "}
                buchen, um zusätzliche Funktionen freizuschalten.
              </p>
            </>
          );
        } else if (status === "active") {
          title = "Aktives Abonnement";
          statusText = "Status: Aktiv";
          statusColor = "tw-text-green-700";
          details = `Beginn: ${startDate}\nAktuelle Periode: ${periodStart} – ${periodEnd}\nZahlungsintervall: ${user?.subscription?.billing_interval === "year" ? "jährlich" : "monatlich"}\nPreis: ${user?.subscription?.price_display ?? "-"}`;
          showCancel = true;
          // Kündigungs-/Wechselabschnitt harmonisch gestalten:
          extraDetails = (
            <>
              {details && (
                <p className="text-sm text-gray-500 mb-1" style={{ whiteSpace: "pre-line" }}>{details}</p>
              )}
              <p className="text-sm text-gray-500 mb-1">
                Sie können Ihr Abo jederzeit erweitern oder auf ein anderes Modell wechseln –{" "}
                <button
                  type="button"
                  onClick={scrollToPlans}
                  className="text-primary-600 underline hover:text-primary-800"
                >
                  hier klicken
                </button>.
              </p>
              {user?.subscription?.cancellation_pending ? (
                <p className="tw-mt-2 tw-text-sm tw-text-gray-600">
                  Ihre Kündigung ist vorgemerkt. Das Abonnement läuft noch bis {periodEnd} weiter.
                </p>
              ) : (
                <>

                  <button
                    type="button"
                    onClick={() => setShowCancelDialog(true)}
                    className="tw-text-red-600 tw-font-semibold tw-mt-1 hover:tw-underline tw-text-left"
                  >
                    Abo kündigen
                  </button>
                </>
              )}
            </>
          );
        } else if (["past_due", "unpaid"].includes(status)) {
          title = "Zahlung ausstehend";
          statusText = "Ihre Zahlung ist überfällig oder fehlgeschlagen. Bitte prüfen Sie Ihre Zahlungsdaten.";
          statusColor = "tw-text-yellow-800";
          details = "";
          extraButtons = (
            <button
              type="button"
              onClick={() => (window.location.href = "/api/payments/create-portal-session/")}
              className="_button_19rto_40 _primary_19rto_127 _ghost_19rto_151 tw-text-base tw-mt-2"
            >
              Zahlungsdaten aktualisieren
            </button>
          );
        } else if (status === "canceled") {
          title = "Abo beendet";
          statusText = `Ihr Abonnement wurde gekündigt und endet am ${periodEnd}.`;
          statusColor = "tw-text-gray-600";
          details = "";
        } else if (status === "paused") {
          title = "Abo pausiert";
          statusText = "Ihr Abo ist derzeit pausiert. Bitte wenden Sie sich an den Support, um es wieder zu aktivieren.";
          statusColor = "tw-text-gray-700";
          details = "";
        } else if (status === "pending_update") {
          title = "Abo wird aktualisiert";
          statusText = "Ihr Abonnement wird gerade geändert. Bitte warten Sie einen Moment.";
          statusColor = "tw-text-blue-700";
          details = "";
        } else if (status === "incomplete_expired") {
          title = "Abo abgelaufen";
          statusText = "Ihre Buchung wurde nie abgeschlossen. Bitte wählen Sie ein neues Paket.";
          statusColor = "tw-text-red-700";
          details = "";
        } else {
          // Fallback: kein Status
          return null;
        }

        return (
          <div className="tw-flex tw-flex-col tw-gap-1 tw-my-5 tw-text-left tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-p-4 tw-shadow-sm">
            <h2 className="text-lg font-bold mb-2">{title}</h2>
            {isProbeModel ? (
              <>
                <p className="text-sm text-gray-700 mb-1">Probe-Abo: {planName}</p>
                <p className="text-sm text-green-600 font-semibold">
                  Ihre Testphase läuft seit {periodStart} und endet am {periodEnd}.
                </p>
                <p className="text-sm text-gray-500 mb-1">
Dieses Probe-Abo dient zur Erprobung unserer Funktionen und endet automatisch, ohne dass Kosten entstehen oder eine Verlängerung erfolgt. Während der Testphase ist eine Buchung regulärer Abo-Modelle noch nicht möglich. Weitere Informationen zu unseren Preisen finden Sie unter{" "}
<a
  href="https://lumanote.de/pricing"
  target="_blank"
  rel="noreferrer"
  className="text-primary-600 underline hover:text-primary-800"
>
  lumanote.de/pricing
</a>
. Bei Interesse können Sie sich jederzeit gerne vorab an uns wenden.                </p>
                {!isPilot && (

                  <p className="text-sm text-gray-500">
                    Sie können jederzeit ein {" "}
                    <button
                      type="button"
                      onClick={scrollToPlans}
                      className="text-primary-600 underline hover:text-primary-800"
                    >
                      reguläres Abo-Modell
                    </button>
                    {" "} buchen, wenn Sie mit LumaNote zufrieden sind.
                  </p>)}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-1">Abo-Modell: {planName}</p>
                <p className={`text-sm mb-1 font-semibold ${statusColor}`}>{statusText}</p>
                {extraDetails}
                {extraButtons}
              </>
            )}
          </div>
        );
      })()}
      {user?.stripe_customer_id && (
        <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-p-4 tw-shadow-sm tw-my-5 tw-flex tw-flex-col tw-gap-1">
          <h2 className="text-lg font-bold mb-2">Abonnement verwalten oder ändern</h2>
          <p className="text-sm text-gray-700 mb-1">Rechnungen herunterladen oder Zahlungsinformationen aktualisieren</p>
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-w-full tw-justify-between">
            <p className="tw-text-gray-500 lg:tw-w-[70%] tw-w-full tw-text-sm">
              Sie möchten eine Rechnung herunterladen oder Ihre Zahlungsdaten anpassen? Wir nutzen Stripe für die sichere Zahlungsabwicklung. Klicken Sie auf „Abrechnung verwalten“, um fortzufahren.
            </p>
            <button
              type="button"
              onClick={() => window.location.href = "/api/payments/create-portal-session/"}
              className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
              style={{
                backgroundColor: "#40937a",
                color: "white",
                border: "none"
              }}
            >
              <div className="_btnFlex_19rto_32 text-sm">Zahlungsdetails verwalten</div>
            </button>
          </div>
        </div>
      )}
    {!isPilot && (

      <div className="flex items-start">
        <div className="payment">
          {selectedPlan ? (
            showCheckoutForm ? (
              <div className="payment__portal__checkout">
                <div id="payment-form" data-testid="payment-form">
                  <div className="flex gap-6 w-full">
                    <img
                      src="/logo_only.png"
                      className="block h-[80px] w-[80px]"
                      alt="Mobiles Logo"
                    />
                    <div className="flex flex-col gap-1">
                      <p className="text-[#1A1A1A99] text-base m-0">Sie abonnieren Sie das {selectedPlan?.name}</p>
                      <p className="plans-info flex flex-col">
                        <span className="text-black font-extrabold text-2xl">{selectedPlan?.price_eur} </span>
                        <span className="text-[#1A1A1A99] text-sm"> / monatlich abgerechnet</span>
                      </p>
                    </div>
                  </div>
                  <div className="border border-solid border-gray-200 p-4 rounded-lg min-h-[300px]">
                    <p className="text-black font-bold text-base">Checkout</p>
                    <div className="StripeElement">
                      <Elements key="checkout" stripe={stripePromise}>
                        <CheckoutForm user={user} />
                      </Elements>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // keep existing original summary block here
              <div className="payment__portal__create">
                <div className="flex gap-6 w-full">
                  <img
                    src="/logo_only.png"
                    className="block h-[80px] w-[80px]"
                    alt="Mobiles Logo"
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-[#1A1A1A99] text-base m-0">Abonnieren Sie das {selectedPlan?.name}</p>
                    <p className="plans-info flex flex-col">
                      <span className="text-black font-extrabold text-2xl">{selectedPlan?.price_eur} </span>
                      <span className="text-[#1A1A1A99] text-sm"> / monatlich abgerechnet </span>
                    </p>
                  </div>
                </div>
                <div className="flex sm:gap-10 gap-4 my-2 pt-4 w-full" style={{ borderTop: "1px solid rgb(229, 229, 229)" }}>
                  <p className="flex items-center text-md text-base font-extrabold m-0">{selectedPlan?.name.toUpperCase()}</p>
                  <div className="flex gap-2 text-nowrap">
                    <div className="subscription-toggle year-active">
                      <div className="subscription-toggle-inner yearly-active"></div>
                    </div>
                    <p className="flex items-center bg-[#D5F6DE] m-0  rounded-sm px-1"> Spare 15 %</p>
                    <p className="flex items-center m-0 text-gray-500"> bei jährlicher Zahlung</p>
                  </div>
                </div>
                <p className="m-0 py-2 text-gray-500 font-medium pb-4" style={{ borderBottom: "1px solid rgb(229, 229, 229)" }}>{selectedPlan?.description}</p>
                <div className="payment__portal__create__sub__total">
                  <div className="sub_total">
                    <p className="m-0">Zwischensumme</p>
                    <p className="m-0">{selectedPlan?.price_eur}</p>
                  </div>
                  <div>
                    <button className="text-primary-500 flex my-6 !text-[12.5px]" style={{ width: "fit-content" }}> Promotion-Code hinzufügen </button>
                  </div>
                </div>
                <div className="payment__portal__create__total">
                  <p>Gesamtbetrag heute fällig</p>
                  <p>{selectedPlan?.price_eur}</p>
                </div>
                <div>
                  {!showCheckoutForm && (
                    <button
                      type="button"
                      id=""

                      className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !font-normal"
                      style={{
                        backgroundImage: "linear-gradient(to right,rgb(13, 55, 69),rgb(76, 146, 170),rgb(121, 172, 188))",
                        color: "white",
                        border: "none"
                      }}
                      onClick={() => setShowCheckoutForm(true)}
                    >
                      <div className="_btnFlex_1uoax_32">Weiter zur Kasse</div>
                    </button>
                  )}
                  <div className="flex flex-col gap-2 pt-3 w-full">
                    <div className="text-[#20A961] gap-1 flex items-center justify-center w-full">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame"><path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M8 1C7.07174 1 6.1815 1.36875 5.52513 2.02513C4.86875 2.6815 4.5 3.57174 4.5 4.5V6.5C3.96957 6.5 3.46086 6.71071 3.08579 7.08579C2.71071 7.46086 2.5 7.96957 2.5 8.5V13C2.5 13.5304 2.71071 14.0391 3.08579 14.4142C3.46086 14.7893 3.96957 15 4.5 15H11.5C12.0304 15 12.5391 14.7893 12.9142 14.4142C13.2893 14.0391 13.5 13.5304 13.5 13V8.5C13.5 7.96957 13.2893 7.46086 12.9142 7.08579C12.5391 6.71071 12.0304 6.5 11.5 6.5V4.5C11.5 2.56667 9.93333 1 8 1ZM10.5 6.5V4.5C10.5 3.83696 10.2366 3.20107 9.76777 2.73223C9.29893 2.26339 8.66304 2 8 2C7.33696 2 6.70107 2.26339 6.23223 2.73223C5.76339 3.20107 5.5 3.83696 5.5 4.5V6.5H10.5Z" fill="#20A961"></path></g></svg>
                      <span className="font-bold text-base"> Sicherer Checkout</span>
                    </div>
                    <div className="flex items-center justify-center w-full">
                      <p className="text-black m-0">
                        <span className="mr-2 text-md font-normal">Powered by STRIPE</span>
                        <svg width="47" height="19" viewBox="0 0 47 19" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="..." fill="#040404" /></svg>
                        <span className="font-normal"> - </span>
                        <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="" target="_blank" rel="noreferrer">Nutzungsbedingungen</a></span>
                        <span className="font-normal"> &amp; </span>
                        <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="" target="_blank" rel="noreferrer">Datenschutz</a></span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )
          ) : (

            <div ref={plansRef} className="flex flex-col md:flex-row justify-center gap-2 scroll-smooth">
              {plans.map((plan, index) => {
                const baseColor = plan.color ?? "#32768d";
                const hoverColor = shadeColor(baseColor, -15);
                const icon = iconMap[plan.icon_name] || <StarIcon fontSize="small" className="text-white" />;
                const isRecommended = plan.name === "Basis Paket";
                // Neue Logik für Planstatus
                const currentPlanRank = user?.subscription
                  ? plans.find(p => p.name.toLowerCase() === (user?.subscription?.plan_name ?? "Kein Plan").toLowerCase())?.rank || 0
                  : 0;
                const targetPlanRank = plan.rank;
                const isCurrentPlan = targetPlanRank === currentPlanRank;
                const isUpgrade = targetPlanRank > currentPlanRank;
                const isDowngrade = targetPlanRank < currentPlanRank;
                // Dynamische Border-Farbe
                return (
                  <div
                    key={index}
                    className={`relative w-[18rem] h-full rounded-2xl shadow-lg p-2 bg-white${isCurrentPlan ? " opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {/* Runde Bögen als Rahmen */}

                    {/* Inhalt mit Abstand damit Bögen sichtbar bleiben */}
                    <div
                      className={`relative z-10 bg-white rounded-2xl p-6 pt-8 pb-8 border-2 border-b-0`}
                      style={{ borderColor: plan.color }}
                    >
                      {isRecommended && (
                        <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                          Empfohlen
                        </div>
                      )}
                      <div style={{ backgroundColor: plan.color }} className="p-2 rounded-full mb-4 mx-auto w-fit">
                        <div className="w-6 h-6 flex items-center justify-center">{icon}</div>
                      </div>
                      <h3 className="text-xl font-bold text-center mb-1">{plan.name}</h3>
                      {plan.headline && (
                        <p style={{ color: plan.color }} className="font-semibold text-center mb-4">
                          {plan.headline}
                        </p>
                      )}
                      <p className="text-sm text-gray-700 text-center mb-4">{plan.description}</p>
                      <ul className="text-left text-sm text-gray-600 mb-4 space-y-2 pl-1">
                        {plan.features.map((feature, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 pr-2 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:outline-none hover:shadow-none transition-none"
                          >
                            <div style={{ backgroundColor: plan.color }} className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="text-center mb-3">
                        <p className="text-sm text-gray-700 mb-1">nur</p>
                        <p className="text-xl font-bold mb-0">
                          {plan.price_eur.replace('.', ',')}
                          <span className="text-base font-normal">/ Monat</span>
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">inkl. MwSt.</p>
                      </div>
                      <div className="flex flex-col space-y-2 items-center mt-4">
                        <div className="flex items-center space-x-2">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                          <span className="text-gray-500 text-sm">Sichere Zahlung mit Stripe</span>
                        </div>
                        <div className="bg-gray-100 rounded-lg px-4 py-2 flex flex-col items-center w-full">
                          <div className="text-xs text-gray-600 mb-3 text-center leading-snug">
                            <p className="m-0">Monatlich kündbar</p>
                            <p className="m-0">Keine Mindestlaufzeit</p>
                          </div>
                          {isCurrentPlan ? (
                            <button
                              type="button"
                              disabled
                              className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 opacity-60 cursor-not-allowed"
                              style={{ backgroundColor: plan.color ?? "#32768d" }}
                            >
                              <div className="_btnFlex_19rto_32">Aktueller Plan</div>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handlePlanSelect(plan)}
                              className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                              style={{ backgroundColor: plan.color ?? "#32768d" }}
                            >
                              <div className="_btnFlex_19rto_32">
                                {"Buchen"}
                              </div>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>)}
      {/* Modal für Kündigungsbestätigung */}
      {showCancelDialog && (
        <ModalWrapper
          onClose={() => {
            setShowCancelDialog(false);
            setCancelConfirmed(false);
          }}
          title="Bestätigung der Kündigung Ihres Abos"
        >
          <div>
            <p className="tw-text-sm tw-leading-[24px] tw-text-gray-500 text-center">
              Wenn Sie Ihr Abonnement kündigen, bleibt es bis zum Ende des aktuellen Abrechnungszeitraums aktiv. Danach wird es automatisch beendet. Dieser Vorgang kann nicht rückgängig gemacht werden.
            </p>
            <div className="tw-flex tw-grow md:tw-grow-0 tw-flex-col tw-mb-4">
              <label className="tw-inline-flex tw-space-x-3 tw-cursor-pointer tw-text-xs sm:tw-text-sm lg:tw-text-base tw-my-5 tw-items-start">
                <input
                  required
                  className="tw-h-4 tw-w-4 tw-border tw-border-gray-300 tw-rounded tw-cursor-pointer checked:bg-[#40937a]"
                  type="checkbox"
                  value="agree"
                  checked={cancelConfirmed}
                  onChange={() => setCancelConfirmed(!cancelConfirmed)}
                />
                <p className="-tw-m-1 tw-text-gray-500 tw-text-sm">
                  Ich bestätige, dass ich mein Abonnement kündigen möchte.
                </p>
              </label>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2">
              <button
                disabled={!cancelConfirmed}
                onClick={handleCancelSubscription}
                className={`tw-inline-flex tw-w-full md:tw-w-fit tw-px-3 tw-py-2 tw-ml-auto tw-rounded tw-shadow tw-space-x-2 tw-border-0 tw-items-center tw-justify-center tw-text-left ${!cancelConfirmed
                  ? 'tw-bg-danger-500/60 text-white tw-cursor-not-allowed'
                  : 'tw-bg-danger-500 text-white tw-cursor-pointer'
                  }`}
              >
                Abo kündigen
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default PlanPicker;